import { HomePage } from 'graphql-common';
import mergeAndDestroyPartialObjects from '@lib/utils/mergeAndDestroyPartialObjects';
import { Values } from '@lib/interfaces/form';

interface TabInput {
  id?: string;
  name: string;
}

interface TabForUpdate {
  id?: string;
  name: string;
  position: number;
}

interface PrepareTabsOptions {
  isViewOnly?: boolean;
}

/**
 * Prepares tabs data for bulk update mutation
 * Converts form values and existing home pages into the format expected by the API
 *
 * @param values - Form values containing tabs array
 * @param homePages - Existing home pages from the query
 * @param options - Options for filtering (e.g., isViewOnly to exclude default tabs)
 * @returns Array of changes for bulk update, or null if no changes
 */
export default function prepareTabsForBulkUpdate(
  values: Values,
  homePages: Pick<HomePage, 'id' | 'name' | 'position' | 'isDefault'>[],
  options: PrepareTabsOptions = {},
) {
  const { isViewOnly = false } = options;

  // Get IDs of default tabs to exclude when in view-only mode
  const defaultTabIds = isViewOnly
    ? new Set(homePages.filter((page) => page.isDefault).map((page) => page.id))
    : new Set<string>();

  const newTabs = (values.tabs as TabInput[])
    .map((tab, index) => ({
      id: tab.id,
      name: tab.name,
      position: index,
    }))
    .filter((tab) => !isViewOnly || !tab.id || !defaultTabIds.has(tab.id));

  const oldItems: TabForUpdate[] = homePages
    .filter((page) => !isViewOnly || !page.isDefault)
    .map((page) => ({
      id: page.id,
      name: page.name ?? '',
      position: page.position,
    }));

  return mergeAndDestroyPartialObjects(oldItems, newTabs);
}
